/**
 * useJobStatus Hook
 * React Query hook for fetching and polling transcription job status
 */

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { reportImportTimingOnComplete } from '../../services/clientImportTiming';
import { transcriptionService } from '../../services/transcriptionService';
import { usePollingEnabled } from '../usePollingEnabled';

import type {
  JobStatusResponse,
  TranscriptionJobStatus,
} from '../../types/transcription';

interface UseJobStatusOptions
  extends Omit<
    UseQueryOptions<JobStatusResponse, Error>,
    'queryKey' | 'queryFn'
  > {
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const useJobStatus = (
  requestId: number | undefined,
  options?: UseJobStatusOptions,
) => {
  // Check if polling should be enabled (considers network status and visibility)
  const pollingEnabled = usePollingEnabled();

  // Extract refetchInterval from options to prevent it from overriding our function
  const { refetchInterval: optionsRefetchInterval, ...restOptions } =
    options || {};

  const query = useQuery<JobStatusResponse, Error>({
    queryKey: ['transcription-job-status', requestId],
    queryFn: () => {
      if (!requestId) {
        throw new Error('Request ID is required');
      }
      return transcriptionService.getJobStatus(requestId);
    },
    // Disable queries when offline to prevent network errors
    enabled: !!requestId && options?.enabled !== false && pollingEnabled,
    // Force fresh data when query mounts - don't use stale cached "completed" status
    staleTime: 0,
    refetchOnMount: 'always',
    // Retry logic: don't retry on "Job not found" errors (404) or network errors when offline
    retry: (failureCount, error) => {
      // Don't retry if job not found - this is expected for non-transcription requests
      if (error instanceof Error && error.message.includes('Job not found')) {
        return false;
      }
      // Don't retry network errors - let polling handle reconnection
      if (error instanceof Error && error.message.includes('Network error')) {
        return false;
      }
      // Default retry behavior for other errors
      return failureCount < 3;
    },
    refetchInterval: query => {
      // Stop polling when offline
      if (!pollingEnabled) {
        return false;
      }
      // Stop polling when job is completed or failed.
      // NOTE: In TanStack Query v5 the refetchInterval callback receives the
      // full Query object, not the data — read status off query.state.data.
      const status = query.state.data?.status;
      if (
        status === 'completed' ||
        status === 'failed' ||
        status === 'archived'
      ) {
        return false;
      }
      // Use provided refetchInterval or default to 5 seconds for active jobs
      return optionsRefetchInterval ?? 5000;
    },
    ...restOptions,
  });

  // PERF-4: fire the client-perceived-timing beacon once, the moment a poll first
  // observes status === 'completed' (the same instant the "done" UI appears).
  // This is the status transition, not the notification toast — but note the
  // server may have finished up to one poll interval (~5s) earlier; that gap is
  // included in clientTotalMs as real user-perceived wait. Idempotent: the timing
  // tracker only reports the first time per request, so multiple mounted pollers
  // for the same request won't double-report.
  const reportedRef = useRef(false);
  useEffect(() => {
    if (
      requestId &&
      query.data?.status === 'completed' &&
      !reportedRef.current
    ) {
      reportedRef.current = true;
      reportImportTimingOnComplete(requestId);
    }
  }, [requestId, query.data?.status]);

  return query;
};
